import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ozonImage from '~/assets/img/ozon.png';
import { company } from '~/company/Company';
import { HeaderAddressWrapper } from '~/components/MobileHeaderCD/components/HeaderAddressWrapper';
import {
  HEADER_ORDER_STATUSES,
  HeaderOrders,
} from '~/components/MobileHeaderCD/components/HeaderOrders';

import { useMounted } from '~/hooks/useMounted';
import { orderStore } from '~/stores';

import HeaderIconBanner from '../HeaderIconBanner/HeaderIconBanner';

import BannersDropdown from './components/BannersDropdown';
import { CatalogButton } from './components/CatalogButton';
import { CategoriesSubheader } from './components/CategoriesSubheader';
import { ExpressBanner } from './components/ExpressBanner';
import { Logo } from './components/Logo';
import { SearchBar } from './components/SearchBar';
import { UserActions } from './components/UserActions';
import { DESKTOP_HEADER_ID } from './constants';
import styles from './HeaderV3.module.scss';
import { useIsTablet } from './hooks';

type HeaderV3Props = Omit<React.ComponentPropsWithRef<'header'>, 'children'>;

const HeaderV3 = React.forwardRef<HTMLHeadingElement, HeaderV3Props>(
  (props, ref) => {
    const mounted = useMounted();
    const activeOrders = mounted
      ? orderStore.activeOrders?.filter((order) =>
          HEADER_ORDER_STATUSES.includes(order.public_status),
        )
      : [];

    const isTablet = useIsTablet();
    const { t } = useTranslation();
    return (
      <header {...props} ref={ref} className={styles.headerWrapper}>
        {company.showHeaderExpressBanner && <ExpressBanner />}
        <div
          id={DESKTOP_HEADER_ID}
          className={styles.header}
          data-company={company.name}
        >
          <div className={styles.headerContent}>
            <div className={styles.leftSection}>
              <Logo />
              {company.showHeaderCatalogButton && (
                <CatalogButton isTablet={isTablet} />
              )}
            </div>

            <SearchBar />

            <div className={styles.rightSection}>
              {company.showHeaderOrders && (
                <HeaderOrders
                  className={styles.orders}
                  overlayClassName={styles.overlay}
                  dropdownClassName={styles.dropdownOrders}
                  showLabel={!isTablet}
                />
              )}
              {company.showHeaderBanners && (
                // TODO: add more details for banners
                <div className={styles.bannersWrapper}>
                  <BannersDropdown />
                  <HeaderIconBanner
                    imageSrc={ozonImage}
                    imageAlt="Ozon"
                    title={t('phrases:work')}
                    subtitle={`1 ${t('translation:year')}`}
                    variant="gray"
                  />
                </div>
              )}
              {company.showHeaderAddress && !activeOrders.length && (
                <HeaderAddressWrapper
                  className={styles.address}
                  shortTitle={isTablet}
                />
              )}
              {company.showHeaderUserActions && <UserActions />}
            </div>
          </div>
          {company.showSubheaderCategories && (
            <CategoriesSubheader className={styles.subheader} />
          )}
        </div>
      </header>
    );
  },
);
HeaderV3.displayName = 'HeaderV3';

export default observer(HeaderV3);
